// PromptPage.js - ✅ WITH UPGRADE MODAL + LANDING PAGE HERO INPUT HANDLING
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './PromptPage.css';
import { Zap, Crown, Briefcase, Sparkles } from 'lucide-react';
import HelloSentences from '../essentials/HelloSentences';
import { cleanUserInput, validatePromptInput } from '../constants/inputSanitizer';
import { useAuth } from '../AuthContext';
import Logo from '../assets/logo-sv.png';
import { validateAudioFile, formatFileSize, formatDurationDisplay } from '../utils/audioValidation';
import { getPlanLimits } from '../constants/planLimits';
import UpgradeModal from './UpgradeModal';
import { usePlanTracker } from '../hooks/usePlanTracker';
import { promptExamples } from '../constants/promptExamples';

function PromptPage({ 
  audioInputRef, 
  onAudioChange, 
  onPromptGenerate, 
  onLoadPresentation,
  onAudioGenerateWithPrompt,
  isProcessing, 
  processingError 
}) {
  
  const { user } = useAuth();
  const location = useLocation();

  const [promptText, setPromptText] = useState('');
  const [validationAttempts, setValidationAttempts] = useState(0);
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [showExamplesMenu, setShowExamplesMenu] = useState(false);

  // Audio preview states
  const [audioPreview, setAudioPreview] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [audioFileName, setAudioFileName] = useState('');
  const [audioDuration, setAudioDuration] = useState(0);
  
  // Validation states
  const [isValidatingAudio, setIsValidatingAudio] = useState(false);

  // Upgrade modal states
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalConfig, setUpgradeModalConfig] = useState({
    limitType: 'projects',
    suggestedPlan: null
  });

  const { usage, userPlan: currentUserPlan } = usePlanTracker(user?.uid);

  const maxLength = audioFile ? 500 : 5000;
  const charCount = promptText.length;

  // ── Handle incoming state from landing page hero box ──────────────────────
  useEffect(() => {
    const incoming = location.state;
    if (!incoming?.autoSubmit) return;

    // Clear so a page refresh won't re-trigger
    window.history.replaceState({}, document.title);

    if (incoming.heroFile) {
      // File was attached to window before navigate() in LandingPage
      const file = window.__cayotyHeroFile;
      window.__cayotyHeroFile = null;

      if (file) {
        (async () => {
          setValidationError(null);
          setIsValidatingAudio(true);
          try {
            const currentPlan = currentUserPlan || 'free';
            const validation = await validateAudioFile(file, currentPlan);

            if (!validation.valid) {
              if (validation.requiresUpgrade && validation.suggestedPlan) {
                setUpgradeModalConfig({ limitType: 'duration', suggestedPlan: validation.suggestedPlan });
                setShowUpgradeModal(true);
              } else {
                setValidationError({ message: validation.error, showExamples: false, isAudioMode: true });
              }
              return;
            }

            setAudioFile(file);
            setAudioFileName(file.name);
            setAudioDuration(validation.duration);
            const url = URL.createObjectURL(file);
            setAudioPreview(url);

            if (incoming.heroPrompt) {
              setPromptText(incoming.heroPrompt.substring(0, 500));
            }

            // Auto-submit — same as normal audio flow
            onAudioGenerateWithPrompt(file, incoming.heroPrompt || '');
          } finally {
            setIsValidatingAudio(false);
          }
        })();
      }
    } else if (incoming.heroPrompt) {
      // Text prompt only — pre-fill and auto-submit
      setPromptText(incoming.heroPrompt);
      setTimeout(() => {
        const cleaned = incoming.heroPrompt.trim();
        if (cleaned.length >= 3) {
          onPromptGenerate(cleaned);
        }
      }, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount only
  // ─────────────────────────────────────────────────────────────────────────

  const handlePromptSubmit = () => {
    if (!user) {
      setValidationError({
        message: 'Please sign in to generate slides',
        showExamples: false,
        isAudioMode: false
      });
      return;
    }
    setValidationError(null);

    const cleaned = cleanUserInput(promptText, {
      preserveNewlines: false,
      removeInvisibleChars: true,
      normalizeUnicode: true,
      trimWhitespace: true,
      maxConsecutiveSpaces: 1
    });

    if (audioFile) {
      if (cleaned.length > 0) {
        const validation = validatePromptInput(cleaned, { 
          allowShort: true,
          minLength: 0,
          maxLength: 500
        });
        
        if (!validation.isValid) {
          setValidationAttempts(prev => prev + 1);
          
          const errorMessages = {
            'TOO_LONG': 'Instructions are too long. Please keep them under 500 characters.',
            'REPEATED_CHAR': 'Please enter meaningful instructions',
            'KEYBOARD_SPAM': 'Please enter real instructions, not random keyboard input',
            'NO_CONTENT': 'Please enter actual content or leave empty',
            'TOO_MANY_SPECIAL_CHARS': 'Please use fewer special characters'
          };

          const message = errorMessages[validation.reason] || 'Invalid instructions';
          
          setValidationError({
            message,
            showExamples: validationAttempts >= 2,
            isAudioMode: true
          });
          return;
        }
      }
      
      setValidationAttempts(0);
      onAudioGenerateWithPrompt(audioFile, cleaned);
      return;
    }
    
    const validation = validatePromptInput(cleaned, {
      maxLength: 5000
    });
    
    if (!validation.isValid) {
      setValidationAttempts(prev => prev + 1);
      
      const errorMessages = {
        'TOO_SHORT': 'Please enter a topic (at least 3 characters)',
        'TOO_LONG': 'Prompt is too long. Please keep it under 5000 characters.',
        'NO_WORDS': 'Please enter a topic with actual words',
        'REPEATED_CHAR': 'Please enter a meaningful topic',
        'KEYBOARD_SPAM': 'Please enter a real topic, not random keyboard input',
        'NO_CONTENT': 'Please enter actual content',
        'ONLY_NUMBERS': 'Please describe your topic in words, not just numbers',
        'SINGLE_WORD': 'Please provide more details about your topic',
        'TOO_MANY_SPECIAL_CHARS': 'Please use fewer special characters'
      };

      const message = errorMessages[validation.reason] || 'Invalid input';
      
      setValidationError({
        message,
        showExamples: validationAttempts >= 2,
        isAudioMode: false
      });
      return;
    }

    setValidationAttempts(0);
    onPromptGenerate(cleaned);
  };

  const handlePromptKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handlePromptSubmit();
    }
  };

  const handleTextChange = (e) => {
    const newValue = e.target.value;
    
    if (newValue.length > maxLength) {
      return;
    }
    
    setPromptText(newValue);
    
    if (validationError) {
      setValidationError(null);
    }
    
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 250) + 'px';
  };

  const handleAudioChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setValidationError(null);
    setIsValidatingAudio(true);

    try {
      const currentPlan = currentUserPlan || 'free';
      const validation = await validateAudioFile(file, currentPlan);
      
      if (!validation.valid) {
        if (validation.requiresUpgrade && validation.suggestedPlan) {
          setUpgradeModalConfig({
            limitType: 'duration',
            suggestedPlan: validation.suggestedPlan
          });
          setShowUpgradeModal(true);
        } else {
          setValidationError({
            message: validation.error,
            showExamples: false,
            isAudioMode: true,
            requiresUpgrade: false
          });
        }
        
        if (audioInputRef.current) {
          audioInputRef.current.value = '';
        }
        
        setIsValidatingAudio(false);
        return;
      }

      setAudioFile(file);
      setAudioFileName(file.name);
      setAudioDuration(validation.duration);
      
      const url = URL.createObjectURL(file);
      setAudioPreview(url);

      if (promptText.length > 500) {
        setPromptText(promptText.substring(0, 500));
      }

    } catch (error) {
      setValidationError({
        message: 'Failed to validate audio file. Please try again.',
        showExamples: false,
        isAudioMode: true
      });
      
      if (audioInputRef.current) {
        audioInputRef.current.value = '';
      }
    } finally {
      setIsValidatingAudio(false);
    }
  };

  const handleRemoveAudio = () => {
    if (audioPreview) {
      URL.revokeObjectURL(audioPreview);
    }
    setAudioFile(null);
    setAudioFileName('');
    setAudioPreview(null);
    setAudioDuration(0);
    
    if (audioInputRef.current) {
      audioInputRef.current.value = '';
    }
  };

  const handleExampleClick = (example) => {
    setPromptText(example);
    setShowExamplesMenu(false);
    
    setTimeout(() => {
      const textarea = document.querySelector('.prompt-textarea');
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 250) + 'px';
      }
    }, 0);
  };

  const currentPlan = currentUserPlan || 'free';
  const planLimits = getPlanLimits(currentPlan);
  const maxDurationMinutes = Math.floor(planLimits.maxAudioDuration / 60);

  return (
    <>
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentPlan={currentPlan}
        limitType={upgradeModalConfig.limitType}
        usage={usage}
        suggestedPlan={upgradeModalConfig.suggestedPlan}
      />

      <div className="claude-container">
        <div className="content-wrapper">

          {user && (
            <div className="user-plan-display">
              <div className="plan-info-badge">
                {currentPlan === 'starter' && <Zap size={16} />}
                {currentPlan === 'creator' && <Crown size={16} />}
                {currentPlan === 'professional' && <Briefcase size={16} />}
                {currentPlan === 'free' && <Sparkles size={16} />}
                <span className="plan-tier-name">
                  {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} Plan
                </span>
                {currentPlan === 'free' && (
                  <a 
                    href="/pricing-plans"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="plan-upgrade-link"
                  >
                    .Upgrade
                  </a>
                )}
              </div>
            </div>
          )}

          <HelloSentences />

          {/* PROMPT BOX */}
          <div className="prompt-box">
            <div className="textarea-wrapper">
              <textarea
                className="prompt-textarea"
                placeholder={
                  audioFile 
                    ? "Optional: Add instructions for slide generation (e.g., 'Focus on key takeaways', 'Include action items')..." 
                    : "Upload audio/video or describe your presentation topic... (e.g., 'Introduction to Machine Learning', 'Benefits of Remote Work', 'History of Ancient Rome')"
                }
                value={promptText}
                onChange={handleTextChange}
                onKeyDown={handlePromptKeyDown}
                maxLength={maxLength}
                disabled={isProcessing || isValidatingAudio}
                rows="1"
              />

              <div className="textarea-actions">
                <div className="upload-menu-wrapper">
                  <button
                    className="plus-btn"
                    onClick={() => setShowUploadMenu(!showUploadMenu)}
                    disabled={!user || isProcessing || audioFile || isValidatingAudio}
                    title="Upload files"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256">
                      <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
                    </svg>
                  </button>

                  {showUploadMenu && (
                    <div className="upload-dropdown">
                      <button
                        className="dropdown-item"
                        onClick={() => {
                          audioInputRef.current?.click();
                          setShowUploadMenu(false);
                        }}
                        disabled={isProcessing || isValidatingAudio}
                      >
                        <svg className="btn-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                          <line x1="12" x2="12" y1="19" y2="22"/>
                        </svg>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">Upload Audio/Video</span>
                          <p className="upload-hint">Max {maxDurationMinutes} min for {planLimits.name} plan</p>
                        </div>
                      </button>
                    </div>
                  )}
                </div>

                <button
                  className="settings-btn"
                  onClick={() => setShowExamplesMenu(!showExamplesMenu)}
                  disabled={isProcessing || audioFile || isValidatingAudio}
                  title="Prompt Use Cases"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256">
                    <path d="M40,88H73a32,32,0,0,0,62,0h81a8,8,0,0,0,0-16H135a32,32,0,0,0-62,0H40a8,8,0,0,0,0,16Zm64-24A16,16,0,1,1,88,80,16,16,0,0,1,104,64ZM216,168H199a32,32,0,0,0-62,0H40a8,8,0,0,0,0,16h97a32,32,0,0,0,62,0h17a8,8,0,0,0,0-16Zm-48,24a16,16,0,1,1,16-16A16,16,0,0,1,168,192Z"></path>
                  </svg>
                </button>

                <button
                  className="send-btn"
                  onClick={handlePromptSubmit}
                  disabled={
                    !user ||
                    isProcessing || 
                    isValidatingAudio ||
                    (!audioFile && promptText.trim().length === 0)
                  }
                  title={
                    !user 
                      ? "Please sign in to generate slides" 
                      : audioFile 
                        ? "Generate Slides from Audio" 
                        : "Generate Slides"
                  }
                >
                  {isProcessing || isValidatingAudio ? (
                    <div className="spinner-small"></div>
                  ) : (
                    <svg 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <path d="M12 19V5M5 12l7-7 7 7"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Audio preview chip */}
            {audioFile && audioPreview && !isValidatingAudio && (
              <div className="prompt-footer">
                <div className="audio-preview-chip">
                  <span className="audio-chip-icon">
                    {audioFile.type.startsWith('video/') ? '🎥' : '🎵'}
                  </span>
                  <div className="audio-chip-info">
                    <div className="audio-chip-name">{audioFileName}</div>
                    <div className="audio-chip-duration">
                      {formatDurationDisplay(audioDuration)} • {formatFileSize(audioFile.size)}
                      <span style={{marginLeft: '8px', opacity: 0.7, fontSize: '0.85em'}}>
                        (Max {maxDurationMinutes} min)
                      </span>
                    </div>
                  </div>
                  <button 
                    className="remove-audio-chip-btn" 
                    onClick={handleRemoveAudio}
                    title="Remove"
                    disabled={isProcessing}
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Char limit indicator */}
          {charCount > maxLength * 0.9 && (
            <div className="char-limit-indicator">
              <span className={charCount >= maxLength ? 'char-count-exceeded' : 'char-count-warning'}>
                {charCount.toLocaleString()} / {maxLength.toLocaleString()} characters
              </span>
            </div>
          )}

          {/* Examples panel */}
          {showExamplesMenu && (
            <div className="examples-panel">
              <div className="examples-panel-header">
                <span className="examples-panel-title">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256">
                    <path d="M40,88H73a32,32,0,0,0,62,0h81a8,8,0,0,0,0-16H135a32,32,0,0,0-62,0H40a8,8,0,0,0,0,16Zm64-24A16,16,0,1,1,88,80,16,16,0,0,1,104,64ZM216,168H199a32,32,0,0,0-62,0H40a8,8,0,0,0,0,16h97a32,32,0,0,0,62,0h17a8,8,0,0,0,0-16Zm-48,24a16,16,0,1,1,16-16A16,16,0,0,1,168,192Z"></path>
                  </svg>
                  {' '}Prompt Use Cases
                </span>
                <button 
                  className="examples-close-btn"
                  onClick={() => setShowExamplesMenu(false)}
                  title="Close"
                >
                  ×
                </button>
              </div>
              <div className="examples-panel-content">
                {promptExamples.map((category, idx) => (
                  <div key={idx} className="examples-category">
                    <div className="examples-category-title">{category.category}</div>
                    {category.examples.map((example, exIdx) => (
                      <button
                        key={exIdx}
                        className="example-item"
                        onClick={() => handleExampleClick(example)}
                        disabled={isProcessing || isValidatingAudio}
                      >
                        {example}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Validating spinner */}
          {isValidatingAudio && (
            <div className="validation-info-card">
              <div className="spinner-small" style={{ marginRight: '8px' }}></div>
              <span>Validating audio file...</span>
            </div>
          )}

          {/* Validation errors */}
          {validationError && !validationError.requiresUpgrade && (
            <div className="validation-error-card">
              <div className="validation-error-content">
                <img src={Logo} alt="Logo" className="validation-error-logo" />
                <div className="validation-error-text">
                  <p className="validation-error-message">{validationError.message}</p>
                  
                  {validationError.showExamples && !validationError.isAudioMode && (
                    <div className="validation-examples">
                      <p className="validation-examples-title">Need help? Try examples like:</p>
                      <ul>
                        <li>"Introduction to Machine Learning"</li>
                        <li>"Benefits of Remote Work"</li>
                        <li>"History of Ancient Rome"</li>
                      </ul>
                    </div>
                  )}
                  {validationError.isAudioMode && (
                    <p className="validation-note">Note: Instructions are optional when uploading audio/video.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Processing errors */}
          {processingError && (
            <div className="error-message">
              <span>⚠️</span>
              <span>{processingError}</span>
            </div>
          )}
        </div>

        <input
          ref={audioInputRef}
          type="file"
          accept="audio/*,video/*,.mp3,.mp4,.wav,.m4a,.webm,.mov,.avi"
          onChange={handleAudioChange}
          hidden
        />
      </div>
    </>
  );
}

export default PromptPage;